import React, { useState, useEffect } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { Users, Link as LinkIcon, DollarSign, TrendingUp, Plus, Check, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { clearAffiliateConfigCache } from '../hooks/useAffiliateConfig';
import { useBodyLock } from '../hooks/useBodyLock';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

const AffiliationManagement = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState({ commission_rate: 20 });
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'pending', 'approved'

  // 🔧 FIX MOBILE: Body lock pour les modals
  useBodyLock(showGenerateModal || showPaymentModal || showConfigModal);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [affiliatesRes, paymentsRes, statsRes, configRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/affiliation/affiliates`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/affiliation/payments`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/affiliation/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/affiliation/config`, { headers })
      ]);

      if (affiliatesRes.ok) {
        const data = await affiliatesRes.json();
        setAffiliates(data.affiliates || []);
      }

      if (paymentsRes.ok) {
        const data = await paymentsRes.json();
        setPayments(data.payments || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (configRes.ok) {
        const data = await configRes.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const handleApprove = async (userId) => {
    if (!window.confirm('Approuver cet affilié ? Un email de confirmation sera envoyé.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliation/approve/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Affilié approuvé ! Email envoyé.');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur d\'approbation');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleReject = async (userId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    if (reason === null) return; // User clicked cancel
    
    try {
      const token = localStorage.getItem('token');
      const url = `${BACKEND_URL}/api/admin/affiliation/reject/${userId}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Demande rejetée. Email envoyé.');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de rejet');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDelete = async (affiliateId, affiliateName) => {
    if (!window.confirm(`Supprimer l'affilié ${affiliateName} ? Cette action est irréversible.`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliation/${affiliateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Affilié supprimé');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur de suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  // Filter affiliates based on active tab
  const filteredAffiliates = affiliates.filter(aff => {
    if (activeTab === 'pending') return aff.affiliate_status === 'pending';
    if (activeTab === 'approved') return aff.affiliate_status === 'approved';
    return true; // 'all'
  });

  const pendingCount = affiliates.filter(a => a.affiliate_status === 'pending').length;

  if (loading) {
    return <div className="p-8 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div></div>;
  }

  return (
    <div className="p-6">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Affiliés Actifs</p>
            <p className="text-2xl font-bold">{stats.total_affiliates}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Filleuls Payants</p>
            <p className="text-2xl font-bold text-green-600">{stats.paid_referrals}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Commissions Versées</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_paid_out.toFixed(2)}€</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Commissions En Attente</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending_commissions.toFixed(2)}€</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setShowGenerateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Plus className="w-4 h-4" />
          Créer Affilié
        </button>
        <button
          onClick={() => setShowConfigModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Taux: {config.commission_rate}%
        </button>
      </div>

      {/* Affiliates Table */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Affiliés</h3>
            {/* Tabs */}
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Tous ({affiliates.length})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'pending'
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                En Attente ({pendingCount})
              </button>
              <button
                onClick={() => setActiveTab('approved')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'approved'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Approuvés ({affiliates.length - pendingCount})
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredAffiliates.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              {activeTab === 'pending' ? 'Aucune demande en attente' : 'Aucun affilié'}
            </p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affilié</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filleuls</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Commission</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredAffiliates.map((aff) => (
                  <tr key={aff.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{aff.name || 'Sans nom'}</div>
                      <div className="text-sm text-gray-500">{aff.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      {aff.affiliate_status === 'pending' && (
                        <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800">
                          En attente
                        </span>
                      )}
                      {aff.affiliate_status === 'approved' && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Approuvé
                        </span>
                      )}
                      {aff.affiliate_status === 'rejected' && (
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                          Rejeté
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm">{aff.affiliate_code}</span>
                        <button
                          onClick={() => copyToClipboard(`${window.location.origin}/signup?ref=${aff.affiliate_code}`)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div>Total: {aff.referrals_count}</div>
                        <div className="text-green-600">Payants: {aff.paid_referrals}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-semibold">{aff.total_commission.toFixed(2)}€</div>
                        <div className="text-gray-500">Versé: {aff.paid_commission.toFixed(2)}€</div>
                        <div className="text-amber-600">Dû: {aff.pending_commission.toFixed(2)}€</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-2 justify-end flex-wrap">
                        {aff.affiliate_status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(aff.id)}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors shadow-sm"
                              title="Approuver cet affilié"
                            >
                              ✓ Approuver
                            </button>
                            <button
                              onClick={() => handleReject(aff.id)}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors shadow-sm"
                              title="Rejeter cet affilié"
                            >
                              ✗ Rejeter
                            </button>
                          </>
                        )}
                        {aff.affiliate_status === 'approved' && (
                          <>
                            {aff.pending_commission > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedAffiliate(aff);
                                  setShowPaymentModal(true);
                                }}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors shadow-sm"
                                title="Verser la commission"
                              >
                                💶 Verser
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(aff.id, aff.name || aff.email)}
                              className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium transition-colors shadow-sm"
                              title="Supprimer cet affilié"
                            >
                              🗑️ Supprimer
                            </button>
                          </>
                        )}
                        {aff.affiliate_status === 'rejected' && (
                          <button
                            onClick={() => handleDelete(aff.id, aff.name || aff.email)}
                            className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium transition-colors shadow-sm"
                            title="Supprimer cet affilié rejeté"
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                        {!aff.affiliate_status && (
                          <span className="text-xs text-gray-400">Aucune action</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Payment History */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Historique des Paiements</h3>
        </div>
        <div className="overflow-x-auto">
          {payments.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Aucun paiement</p>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affilié</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Méthode</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4">
                      <div className="font-medium">{payment.affiliate_name}</div>
                      <div className="text-sm text-gray-500">{payment.affiliate_email}</div>
                    </td>
                    <td className="px-6 py-4 font-semibold text-green-600">{payment.amount.toFixed(2)}€</td>
                    <td className="px-6 py-4">{payment.payment_method === 'bank_transfer' ? 'Virement' : 'PayPal'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(payment.paid_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showGenerateModal && <GenerateAffiliateModal onClose={() => setShowGenerateModal(false)} onSuccess={fetchData} />}
      {showPaymentModal && <PaymentModal affiliate={selectedAffiliate} onClose={() => setShowPaymentModal(false)} onSuccess={fetchData} />}
      {showConfigModal && <ConfigModal config={config} onClose={() => setShowConfigModal(false)} onSuccess={fetchData} />}
    </div>
  );
};

// Generate Affiliate Modal
const GenerateAffiliateModal = ({ onClose, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliation/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_email: email })
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        toast.success('Affilié créé !');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Créer un Affilié</h3>
        
        {!result ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email de l'utilisateur</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 rounded-lg">Annuler</button>
              <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg">
                {loading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="bg-green-50 p-4 rounded-lg mb-4">
              <p className="text-green-800 font-medium">✅ Lien créé !</p>
              <p className="text-sm text-green-700 mt-2">Code: {result.affiliate_code}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded mb-4">
              <p className="text-xs text-gray-500 mb-1">Lien d'affiliation:</p>
              <p className="text-sm font-mono break-all">{result.affiliate_link}</p>
            </div>
            <button onClick={onClose} className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg">Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
};

// Payment Modal
const PaymentModal = ({ affiliate, onClose, onSuccess }) => {
  const [method, setMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState(affiliate?.pending_commission || 0);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliation/payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          affiliate_id: affiliate.id,
          amount: parseFloat(amount),
          payment_method: method,
          payment_reference: reference
        })
      });

      if (response.ok) {
        toast.success('Paiement enregistré !');
        onSuccess();
        onClose();
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Enregistrer un Paiement</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Affilié</label>
            <input type="text" value={affiliate?.email} disabled className="w-full px-3 py-2 border rounded-lg bg-gray-100" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Montant (€)</label>
            <input
              type="number"
              step="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Méthode</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
              <option value="bank_transfer">Virement bancaire</option>
              <option value="paypal">PayPal</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Référence (optionnel)</label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 rounded-lg">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Config Modal
const ConfigModal = ({ config, onClose, onSuccess }) => {
  const [rate, setRate] = useState(config.commission_rate);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/affiliation/config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ commission_rate: parseFloat(rate) })
      });

      if (response.ok) {
        // Clear the cached affiliate config to force refetch with new rate
        clearAffiliateConfigCache();
        toast.success('✅ Taux mis à jour ! Les pages publiques afficheront le nouveau taux.');
        onSuccess();
        onClose();
      } else {
        toast.error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-xl font-bold mb-4">Configuration Commission</h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Taux de commission (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              required
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
            <p className="text-sm text-gray-500 mt-1">
              Commission par vente: {((22.90 * rate) / 100).toFixed(2)}€
            </p>
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-200 rounded-lg">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg">
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliationManagement;
